/**
 * Urgent-processing ETA math — pure day-rolling behind the UrgencyDialog copy
 * and ETA table. No I/O, no DOM; the dialog feeds in the Sydney clock and the
 * pricing.urgency config.
 *
 * Model: the lab prints on `productionDays` only (default Mon-Fri). An urgent
 * job starts today when today is a production day and the Sydney time is
 * before the cutoff; otherwise it starts on the next production day (so a
 * Friday-after-cutoff or weekend order starts Monday). Sydney pickup is ready
 * on the start day; Wollongong is ONE production day later (printed in Sydney,
 * sent down overnight); shipped orders are printed on the start day.
 */

/** Sydney wall-clock snapshot: ISO weekday (0 = Monday .. 6 = Sunday) + minutes since midnight. */
export interface SydneyNow {
  weekday: number;
  minutes: number;
}

export interface UrgencyEta {
  /** Today is a production day (false drives the "We print Monday to Friday" copy). */
  productionToday: boolean;
  /** Today is a production day AND the cutoff has not passed — printing starts today. */
  startsToday: boolean;
  /** Day printing starts = Sydney pickup ready: "Today" | "Tomorrow" | "Monday"… */
  startLabel: string;
  /** startLabel for mid-sentence use: "today" | "tomorrow" | "Monday"… */
  startLabelLower: string;
  /** Wollongong pickup ready (one production day after the start), same label forms. */
  wollongongLabel: string;
}

/** mon..sun tokens (config order matches Date ISO weekday 0 = Monday). */
const WEEKDAY_TOKENS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const WEEKDAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

/** Fallback when pricing.urgency.productionDays is absent/invalid (older published pricing). */
export const DEFAULT_PRODUCTION_DAYS = ['mon', 'tue', 'wed', 'thu', 'fri'];

/** "13:30" → 810. Malformed input falls back to the seed default 13:30. */
export function parseCutoffMinutes(hhmm: string | undefined): number {
  const [h, m] = (hhmm || '13:30').split(':').map((n) => parseInt(n, 10));
  return (Number.isFinite(h) ? h! : 13) * 60 + (Number.isFinite(m) ? m! : 30);
}

/** Config tokens → ISO weekday index set; falls back to Mon-Fri when absent/invalid. */
function toDaySet(productionDays: string[] | undefined): Set<number> {
  const source = Array.isArray(productionDays) ? productionDays : [];
  const days = new Set<number>();
  for (const token of source) {
    const idx = WEEKDAY_TOKENS.indexOf(String(token).trim().toLowerCase());
    if (idx >= 0) days.add(idx);
  }
  return days.size > 0 ? days : new Set(DEFAULT_PRODUCTION_DAYS.map((t) => WEEKDAY_TOKENS.indexOf(t)));
}

/** Offset (days from today) of the first production day STRICTLY AFTER `after`. */
function nextProductionOffset(weekday: number, after: number, days: Set<number>): number {
  for (let i = after + 1; i <= after + 7; i++) {
    if (days.has((weekday + i) % 7)) return i;
  }
  return after + 1; // unreachable — toDaySet never returns an empty set
}

/** 0 → Today/today, 1 → Tomorrow/tomorrow, further out → the weekday name. */
function dayLabel(offset: number, weekday: number, capitalised: boolean): string {
  if (offset === 0) return capitalised ? 'Today' : 'today';
  if (offset === 1) return capitalised ? 'Tomorrow' : 'tomorrow';
  return WEEKDAY_NAMES[(weekday + offset) % 7]!;
}

/**
 * Compute the day-aware urgency ETA from the Sydney clock, the cutoff
 * (minutes since midnight) and the configured production days.
 */
export function computeUrgencyEta(
  now: SydneyNow,
  cutoffMinutes: number,
  productionDays?: string[],
): UrgencyEta {
  const days = toDaySet(productionDays);
  const productionToday = days.has(now.weekday);
  const startsToday = productionToday && now.minutes < cutoffMinutes;
  const startOffset = startsToday ? 0 : nextProductionOffset(now.weekday, 0, days);
  const gongOffset = nextProductionOffset(now.weekday, startOffset, days);
  return {
    productionToday,
    startsToday,
    startLabel: dayLabel(startOffset, now.weekday, true),
    startLabelLower: dayLabel(startOffset, now.weekday, false),
    wollongongLabel: dayLabel(gongOffset, now.weekday, true),
  };
}

/**
 * RIGHT NOW on the lab's clock (Sydney via Intl), regardless of where the
 * viewer is — the cutoff is a Sydney time, not the browser's local. Not pure;
 * feed the result into computeUrgencyEta.
 */
export function sydneyNow(): SydneyNow {
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Australia/Sydney',
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(new Date());
    const wd = (parts.find((p) => p.type === 'weekday')?.value ?? 'Mon').toLowerCase().slice(0, 3);
    const hh = Number(parts.find((p) => p.type === 'hour')?.value ?? '0');
    const mm = Number(parts.find((p) => p.type === 'minute')?.value ?? '0');
    const weekday = WEEKDAY_TOKENS.indexOf(wd);
    return { weekday: weekday >= 0 ? weekday : 0, minutes: hh * 60 + mm };
  } catch {
    return { weekday: 0, minutes: 0 };
  }
}
